import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  IconButton,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Badge,
  Avatar,
  Snackbar,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  FilterList,
  Download,
  NotificationsActive,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  Person,
  DirectionsCar,
  Security,
  Assignment,
  MoreVert,
  Refresh,
  Search,
  Settings,
  Email,
  Sms,
  GetApp,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';

interface ComplianceMetric {
  id: string;
  title: string;
  value: number;
  percentage: number;
  trend: number;
  trendDirection: 'up' | 'down';
  icon: React.ReactNode;
  color: string;
  description: string;
}

interface RegionalCompliance {
  region: string;
  overall: number;
  identity: number;
  license: number;
  vehicle: number;
  insurance: number;
  trend: number;
  flag: string;
}

interface VerificationItem {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  type: 'identity' | 'license' | 'vehicle' | 'insurance';
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  priority: 'high' | 'medium' | 'low';
  submittedAt: string;
  expiresAt?: string;
  region: string;
  documents: string[];
}

interface ComplianceAlert {
  id: string;
  type: 'expiring' | 'regulatory' | 'system';
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  count: number;
  actionRequired: boolean;
  timestamp: string;
}

const ComplianceMetricsPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [bulkActionAnchor, setBulkActionAnchor] = useState<null | HTMLElement>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' as 'success' | 'error' });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    sms: true,
    push: false,
    recipients: 'filtered' as 'filtered' | 'all' | 'pending' | 'expired'
  });
  const [regionalDetailsOpen, setRegionalDetailsOpen] = useState(false);
  const [selectedRegionalData, setSelectedRegionalData] = useState<RegionalCompliance | null>(null);

  // Sample data - in real implementation, this would come from API
  const complianceMetrics: ComplianceMetric[] = [
    {
      id: 'identity',
      title: 'Identity Verification',
      value: 2847,
      percentage: 87.3,
      trend: 2.4,
      trendDirection: 'up',
      icon: <Person />,
      color: '#2196F3',
      description: 'National ID and photo verification'
    },
    {
      id: 'license',
      title: 'Driver License',
      value: 2634,
      percentage: 80.7,
      trend: -1.2,
      trendDirection: 'down',
      icon: <Assignment />,
      color: '#FF9800',
      description: 'Valid driver license verification'
    },
    {
      id: 'vehicle',
      title: 'Vehicle Registration',
      value: 2921,
      percentage: 89.5,
      trend: 3.1,
      trendDirection: 'up',
      icon: <DirectionsCar />,
      color: '#4CAF50',
      description: 'Vehicle registration documents'
    },
    {
      id: 'insurance',
      title: 'Insurance Coverage',
      value: 2756,
      percentage: 84.4,
      trend: 1.8,
      trendDirection: 'up',
      icon: <Security />,
      color: '#9C27B0',
      description: 'Valid insurance certificates'
    },
  ];

  const regionalCompliance: RegionalCompliance[] = [
    {
      region: 'Lagos',
      overall: 86.3,
      identity: 89.1,
      license: 78.4,
      vehicle: 88.7,
      insurance: 85.2,
      trend: 2.1,
      flag: '🇳🇬'
    },
    {
      region: 'Nairobi',
      overall: 87.7,
      identity: 91.2,
      license: 82.3,
      vehicle: 90.1,
      insurance: 87.8,
      trend: 1.4,
      flag: '🇰🇪'
    },
    {
      region: 'Accra',
      overall: 82.5,
      identity: 85.7,
      license: 76.2,
      vehicle: 84.9,
      insurance: 82.1,
      trend: -0.8,
      flag: '🇬🇭'
    },
  ];

  const verificationQueue: VerificationItem[] = [
    {
      id: '1',
      userId: 'user_001',
      userName: 'John Doe',
      userAvatar: '/api/placeholder/40/40',
      type: 'license',
      status: 'pending',
      priority: 'high',
      submittedAt: '2024-01-15T10:30:00Z',
      expiresAt: '2024-01-22T10:30:00Z',
      region: 'Lagos',
      documents: ['license_front.jpg', 'license_back.jpg']
    },
    {
      id: '2',
      userId: 'user_002',
      userName: 'Jane Smith',
      userAvatar: '/api/placeholder/40/40',
      type: 'identity',
      status: 'pending',
      priority: 'medium',
      submittedAt: '2024-01-15T09:15:00Z',
      region: 'Nairobi',
      documents: ['national_id.jpg', 'selfie.jpg']
    },
    {
      id: '3',
      userId: 'user_003',
      userName: 'Mike Johnson',
      userAvatar: '/api/placeholder/40/40',
      type: 'vehicle',
      status: 'expired',
      priority: 'high',
      submittedAt: '2024-01-10T14:20:00Z',
      expiresAt: '2024-01-15T14:20:00Z',
      region: 'Accra',
      documents: ['registration.pdf']
    },
    {
      id: '4',
      userId: 'user_004',
      userName: 'Sarah Williams',
      userAvatar: '/api/placeholder/40/40',
      type: 'insurance',
      status: 'approved',
      priority: 'low',
      submittedAt: '2024-01-14T08:45:00Z',
      region: 'Lagos',
      documents: ['insurance_cert.pdf']
    },
    {
      id: '5',
      userId: 'user_005',
      userName: 'Ahmed Hassan',
      userAvatar: '/api/placeholder/40/40',
      type: 'license',
      status: 'rejected',
      priority: 'medium',
      submittedAt: '2024-01-15T11:20:00Z',
      region: 'Lagos',
      documents: ['license_front.jpg']
    },
    {
      id: '6',
      userId: 'user_006',
      userName: 'Mary Okafor',
      userAvatar: '/api/placeholder/40/40',
      type: 'identity',
      status: 'approved',
      priority: 'high',
      submittedAt: '2024-01-14T15:30:00Z',
      region: 'Lagos',
      documents: ['national_id.jpg', 'selfie.jpg']
    },
    {
      id: '7',
      userId: 'user_007',
      userName: 'David Kimani',
      userAvatar: '/api/placeholder/40/40',
      type: 'vehicle',
      status: 'pending',
      priority: 'low',
      submittedAt: '2024-01-15T07:00:00Z',
      region: 'Nairobi',
      documents: ['registration.pdf', 'inspection.pdf']
    },
    {
      id: '8',
      userId: 'user_008',
      userName: 'Grace Mwangi',
      userAvatar: '/api/placeholder/40/40',
      type: 'insurance',
      status: 'pending',
      priority: 'high',
      submittedAt: '2024-01-15T12:15:00Z',
      expiresAt: '2024-01-20T12:15:00Z',
      region: 'Nairobi',
      documents: ['insurance_cert.pdf']
    },
    {
      id: '9',
      userId: 'user_009',
      userName: 'Kwame Mensah',
      userAvatar: '/api/placeholder/40/40',
      type: 'license',
      status: 'approved',
      priority: 'medium',
      submittedAt: '2024-01-13T10:00:00Z',
      region: 'Accra',
      documents: ['license_front.jpg', 'license_back.jpg']
    },
    {
      id: '10',
      userId: 'user_010',
      userName: 'Ama Boateng',
      userAvatar: '/api/placeholder/40/40',
      type: 'identity',
      status: 'pending',
      priority: 'low',
      submittedAt: '2024-01-15T13:45:00Z',
      region: 'Accra',
      documents: ['national_id.jpg', 'selfie.jpg']
    },
    {
      id: '11',
      userId: 'user_011',
      userName: 'Kofi Asante',
      userAvatar: '/api/placeholder/40/40',
      type: 'vehicle',
      status: 'rejected',
      priority: 'high',
      submittedAt: '2024-01-14T16:30:00Z',
      region: 'Accra',
      documents: ['registration.pdf']
    },
    {
      id: '12',
      userId: 'user_012',
      userName: 'Chinwe Eze',
      userAvatar: '/api/placeholder/40/40',
      type: 'insurance',
      status: 'expired',
      priority: 'medium',
      submittedAt: '2024-01-10T09:00:00Z',
      expiresAt: '2024-01-14T09:00:00Z',
      region: 'Lagos',
      documents: ['insurance_cert.pdf']
    },
    {
      id: '13',
      userId: 'user_013',
      userName: 'Peter Otieno',
      userAvatar: '/api/placeholder/40/40',
      type: 'license',
      status: 'pending',
      priority: 'medium',
      submittedAt: '2024-01-15T14:00:00Z',
      region: 'Nairobi',
      documents: ['license_front.jpg', 'license_back.jpg']
    },
    {
      id: '14',
      userId: 'user_014',
      userName: 'Fatima Abdullahi',
      userAvatar: '/api/placeholder/40/40',
      type: 'identity',
      status: 'rejected',
      priority: 'low',
      submittedAt: '2024-01-14T11:20:00Z',
      region: 'Lagos',
      documents: ['national_id.jpg']
    },
    {
      id: '15',
      userId: 'user_015',
      userName: 'Joseph Mutua',
      userAvatar: '/api/placeholder/40/40',
      type: 'vehicle',
      status: 'approved',
      priority: 'low',
      submittedAt: '2024-01-13T08:30:00Z',
      region: 'Nairobi',
      documents: ['registration.pdf', 'inspection.pdf']
    },
  ];

  const complianceAlerts: ComplianceAlert[] = [
    {
      id: '1',
      type: 'expiring',
      severity: 'high',
      title: 'Documents Expiring Soon',
      message: '23 driver licenses expiring in 7 days',
      count: 23,
      actionRequired: true,
      timestamp: '2024-01-15T08:00:00Z'
    },
    {
      id: '2',
      type: 'regulatory',
      severity: 'medium',
      title: 'New Compliance Requirements',
      message: 'Updated insurance requirements for Lagos region',
      count: 1,
      actionRequired: true,
      timestamp: '2024-01-15T07:30:00Z'
    },
    {
      id: '3',
      type: 'system',
      severity: 'low',
      title: 'Verification Queue Full',
      message: 'High volume of pending verifications',
      count: 156,
      actionRequired: false,
      timestamp: '2024-01-15T07:00:00Z'
    },
  ];

  // Generate trend data based on selected time range
  const generateTrendData = (range: string) => {
    const data = [];
    const today = new Date();

    switch (range) {
      case '1d':
        for (let i = 23; i >= 0; i--) {
          data.push({
            name: `${23 - i}:00`,
            identity: 85 + Math.random() * 5,
            license: 78 + Math.random() * 5,
            vehicle: 87 + Math.random() * 5,
            insurance: 82 + Math.random() * 5,
          });
        }
        break;
      case '7d':
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          data.push({
            name: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            identity: 85 + Math.random() * 5,
            license: 78 + Math.random() * 5,
            vehicle: 87 + Math.random() * 5,
            insurance: 82 + Math.random() * 5,
          });
        }
        break;
      case '30d':
        for (let i = 29; i >= 0; i -= 3) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          data.push({
            name: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            identity: 85 + Math.random() * 5,
            license: 78 + Math.random() * 5,
            vehicle: 87 + Math.random() * 5,
            insurance: 82 + Math.random() * 5,
          });
        }
        break;
      case '90d':
        for (let i = 89; i >= 0; i -= 10) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          data.push({
            name: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            identity: 85 + Math.random() * 5,
            license: 78 + Math.random() * 5,
            vehicle: 87 + Math.random() * 5,
            insurance: 82 + Math.random() * 5,
          });
        }
        break;
      default:
        data.push(
          { name: 'Jan 1', identity: 85, license: 78, vehicle: 87, insurance: 82 },
          { name: 'Jan 8', identity: 86, license: 79, vehicle: 88, insurance: 83 },
          { name: 'Jan 15', identity: 87, license: 80, vehicle: 89, insurance: 84 }
        );
    }

    return data;
  };

  const trendData = React.useMemo(() => generateTrendData(timeRange), [timeRange, refreshKey]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'approved': return '#4CAF50';
      case 'pending': return '#FF9800';
      case 'rejected': return '#F44336';
      case 'expired': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'pending': return <Schedule />;
      case 'rejected': return <Error />;
      case 'expired': return <Warning />;
      default: return <Schedule />;
    }
  };

  // Handler functions
  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setRefreshKey(prev => prev + 1); // Force regenerate trend data
      setIsLoading(false);
      setSnackbar({ open: true, message: 'Data refreshed successfully', type: 'success' });
    }, 1000);
  };

  const handleExport = (format: string) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `compliance-report-${timestamp}`;

    if (format === 'csv' || format === 'excel') {
      // Generate CSV content
      let csvContent = 'Compliance Metrics Report\n\n';

      // Metrics summary
      csvContent += 'Metric,Value,Percentage,Trend\n';
      filteredMetrics.forEach(metric => {
        csvContent += `${metric.title},${metric.value},${metric.percentage}%,${metric.trend > 0 ? '+' : ''}${metric.trend}%\n`;
      });

      csvContent += '\n\nRegional Compliance\n';
      csvContent += 'Region,Overall,Identity,License,Vehicle,Insurance,Trend\n';
      filteredRegionalCompliance.forEach(region => {
        csvContent += `${region.region},${region.overall}%,${region.identity}%,${region.license}%,${region.vehicle}%,${region.insurance}%,${region.trend > 0 ? '+' : ''}${region.trend}%\n`;
      });

      csvContent += '\n\nVerification Queue\n';
      csvContent += 'User,User ID,Type,Status,Priority,Region,Submitted\n';
      filteredQueue.forEach(item => {
        csvContent += `${item.userName},${item.userId},${item.type},${item.status},${item.priority},${item.region},${formatDate(item.submittedAt)}\n`;
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.${format === 'excel' ? 'csv' : 'csv'}`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({ open: true, message: `Report exported as ${format.toUpperCase()}`, type: 'success' });
    } else if (format === 'pdf') {
      // For PDF, we'll create a printable HTML view
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Compliance Metrics Report - ${timestamp}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #1976d2; }
              h2 { color: #424242; margin-top: 30px; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { padding: 12px; text-align: left; border: 1px solid #ddd; }
              th { background-color: #f5f5f5; font-weight: bold; }
              tr:nth-child(even) { background-color: #fafafa; }
              .summary { margin: 20px 0; }
              .metric { display: inline-block; margin: 10px 20px 10px 0; }
              .metric-value { font-size: 24px; font-weight: bold; color: #1976d2; }
              .metric-label { font-size: 14px; color: #666; }
              @media print {
                button { display: none; }
              }
            </style>
          </head>
          <body>
            <h1>Compliance Metrics Report</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Time Range:</strong> ${timeRange === '1d' ? 'Last 24 hours' : timeRange === '7d' ? 'Last 7 days' : timeRange === '30d' ? 'Last 30 days' : 'Last 90 days'}</p>
            <p><strong>Region:</strong> ${selectedRegion === 'all' ? 'All Regions' : selectedRegion}</p>

            <h2>Compliance Metrics Summary</h2>
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                  <th>Percentage</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                ${filteredMetrics.map(metric => `
                  <tr>
                    <td>${metric.title}</td>
                    <td>${metric.value}</td>
                    <td>${metric.percentage.toFixed(1)}%</td>
                    <td>${metric.trend > 0 ? '+' : ''}${metric.trend}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <h2>Regional Compliance</h2>
            <table>
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Overall</th>
                  <th>Identity</th>
                  <th>License</th>
                  <th>Vehicle</th>
                  <th>Insurance</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                ${filteredRegionalCompliance.map(region => `
                  <tr>
                    <td>${region.flag} ${region.region}</td>
                    <td>${region.overall.toFixed(1)}%</td>
                    <td>${region.identity.toFixed(1)}%</td>
                    <td>${region.license.toFixed(1)}%</td>
                    <td>${region.vehicle.toFixed(1)}%</td>
                    <td>${region.insurance.toFixed(1)}%</td>
                    <td>${region.trend > 0 ? '+' : ''}${region.trend}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <h2>Verification Queue (${filteredQueue.length} items)</h2>
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>User ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Region</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                ${filteredQueue.map(item => `
                  <tr>
                    <td>${item.userName}</td>
                    <td>${item.userId}</td>
                    <td>${item.type}</td>
                    <td>${item.status}</td>
                    <td>${item.priority}</td>
                    <td>${item.region}</td>
                    <td>${formatDate(item.submittedAt)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <button onclick="window.print()" style="margin: 20px 0; padding: 10px 20px; background: #1976d2; color: white; border: none; cursor: pointer; font-size: 16px;">Print / Save as PDF</button>
          </body>
          </html>
        `);
        printWindow.document.close();
      }

      setSnackbar({ open: true, message: 'PDF report opened in new window', type: 'success' });
    }

    setExportDialogOpen(false);
  };

  const handleBulkAction = (action: string) => {
    if (selectedItems.length === 0) {
      setSnackbar({ open: true, message: 'Please select items first', type: 'error' });
      return;
    }
    
    setSnackbar({ open: true, message: `${action} applied to ${selectedItems.length} items`, type: 'success' });
    setSelectedItems([]);
    setBulkActionAnchor(null);
  };

  const handleNotificationSend = () => {
    // Determine recipients based on settings
    let recipients: VerificationItem[] = [];

    switch (notificationSettings.recipients) {
      case 'filtered':
        recipients = filteredQueue;
        break;
      case 'all':
        recipients = verificationQueue;
        break;
      case 'pending':
        recipients = verificationQueue.filter(item => item.status === 'pending');
        break;
      case 'expired':
        recipients = verificationQueue.filter(item => item.status === 'expired');
        break;
    }

    // Build notification channels
    const channels = [];
    if (notificationSettings.email) channels.push('Email');
    if (notificationSettings.sms) channels.push('SMS');
    if (notificationSettings.push) channels.push('Push');

    if (channels.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one notification channel', type: 'error' });
      return;
    }

    if (recipients.length === 0) {
      setSnackbar({ open: true, message: 'No recipients match the selected criteria', type: 'error' });
      return;
    }

    // Simulate sending notifications
    const channelText = channels.join(', ');
    const message = `Sent ${channelText} notifications to ${recipients.length} user${recipients.length > 1 ? 's' : ''}`;

    setSnackbar({ open: true, message, type: 'success' });
    setNotificationDialogOpen(false);
  };

  const handleItemAction = (action: string, itemId: string) => {
    setSnackbar({ open: true, message: `${action} applied successfully`, type: 'success' });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };


  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'high': return '#F44336';
      case 'medium': return '#FF9800';
      case 'low': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Filter regional compliance data based on selected region
  const filteredRegionalCompliance = selectedRegion === 'all'
    ? regionalCompliance
    : regionalCompliance.filter(region => region.region === selectedRegion);

  // Filter verification queue
  const filteredQueue = verificationQueue.filter(item => {
    const matchesSearch = item.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.userId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || item.priority === priorityFilter;
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    const matchesRegion = selectedRegion === 'all' || item.region === selectedRegion;

    return matchesSearch && matchesStatus && matchesPriority && matchesType && matchesRegion;
  });

  // Calculate aggregated metrics based on selected region
  const getFilteredMetrics = () => {
    if (selectedRegion === 'all') {
      return complianceMetrics;
    }

    // Find the selected region's data
    const regionData = regionalCompliance.find(r => r.region === selectedRegion);
    if (!regionData) return complianceMetrics;

    // Update metrics based on region data
    return complianceMetrics.map(metric => {
      let regionPercentage = 0;
      switch (metric.id) {
        case 'identity':
          regionPercentage = regionData.identity;
          break;
        case 'license':
          regionPercentage = regionData.license;
          break;
        case 'vehicle':
          regionPercentage = regionData.vehicle;
          break;
        case 'insurance':
          regionPercentage = regionData.insurance;
          break;
        default:
          regionPercentage = metric.percentage;
      }

      // Calculate value based on percentage (approximate)
      const regionValue = Math.round((regionPercentage / 100) * metric.value);

      return {
        ...metric,
        percentage: regionPercentage,
        value: regionValue,
      };
    });
  };

  const filteredMetrics = getFilteredMetrics();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Compliance Metrics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <MenuItem value="1d">Last 24h</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Region</InputLabel>
            <Select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
              <MenuItem value="all">All Regions</MenuItem>
              <MenuItem value="Lagos">Lagos</MenuItem>
              <MenuItem value="Nairobi">Nairobi</MenuItem>
              <MenuItem value="Accra">Accra</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Download />} onClick={() => setExportDialogOpen(true)}>
            Export
          </Button>
        </Box>
      </Box>

      {/* Compliance Alerts */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Compliance Alerts
        </Typography>
        <Grid container spacing={2}>
          {complianceAlerts.map((alert) => (
            <Grid item xs={12} md={4} key={alert.id}>
              <Alert 
                severity={alert.severity as any}
                action={
                  alert.actionRequired && (
                    <Button size="small" color="inherit">
                      Action Required
                    </Button>
                  )
                }
                sx={{ mb: 1 }}
              >
                <AlertTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {alert.title}
                    <Badge badgeContent={alert.count} color="primary" />
                  </Box>
                </AlertTitle>
                {alert.message}
              </Alert>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Compliance Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {filteredMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    backgroundColor: metric.color + '20',
                    color: metric.color,
                    mr: 2
                  }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {metric.title}
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" gutterBottom>
                  {metric.percentage.toFixed(1)}%
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={metric.percentage}
                    sx={{ flexGrow: 1, mr: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {metric.value}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {metric.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {metric.trendDirection === 'up' ? (
                      <TrendingUp sx={{ color: '#4CAF50', fontSize: 16 }} />
                    ) : (
                      <TrendingDown sx={{ color: '#F44336', fontSize: 16 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: metric.trendDirection === 'up' ? '#4CAF50' : '#F44336',
                        ml: 0.5
                      }}
                    >
                      {metric.trend > 0 ? '+' : ''}{metric.trend}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Trend Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Compliance Trends
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip />
              <Line type="monotone" dataKey="identity" stroke="#2196F3" strokeWidth={2} />
              <Line type="monotone" dataKey="license" stroke="#FF9800" strokeWidth={2} />
              <Line type="monotone" dataKey="vehicle" stroke="#4CAF50" strokeWidth={2} />
              <Line type="monotone" dataKey="insurance" stroke="#9C27B0" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Regional Compliance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Regional Compliance Overview
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Region</TableCell>
                  <TableCell>Overall</TableCell>
                  <TableCell>Identity</TableCell>
                  <TableCell>License</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Insurance</TableCell>
                  <TableCell>Trend</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRegionalCompliance.map((region) => (
                  <TableRow key={region.region}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{region.flag}</span>
                        {region.region}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={region.overall}
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                        />
                        {region.overall.toFixed(1)}%
                      </Box>
                    </TableCell>
                    <TableCell>{region.identity.toFixed(1)}%</TableCell>
                    <TableCell>{region.license.toFixed(1)}%</TableCell>
                    <TableCell>{region.vehicle.toFixed(1)}%</TableCell>
                    <TableCell>{region.insurance.toFixed(1)}%</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {region.trend > 0 ? (
                          <TrendingUp sx={{ color: '#4CAF50', fontSize: 16 }} />
                        ) : (
                          <TrendingDown sx={{ color: '#F44336', fontSize: 16 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: region.trend > 0 ? '#4CAF50' : '#F44336',
                            ml: 0.5
                          }}
                        >
                          {region.trend > 0 ? '+' : ''}{region.trend}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedRegionalData(region);
                          setRegionalDetailsOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Verification Queue */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Verification Queue ({filteredQueue.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {selectedItems.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<MoreVert />}
                  onClick={(e) => setBulkActionAnchor(e.currentTarget)}
                >
                  Bulk Actions ({selectedItems.length})
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<NotificationsActive />}
                onClick={() => setNotificationDialogOpen(true)}
              >
                Notifications
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                <MenuItem value="all">All Priority</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="identity">Identity</MenuItem>
                <MenuItem value="license">License</MenuItem>
                <MenuItem value="vehicle">Vehicle</MenuItem>
                <MenuItem value="insurance">Insurance</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Queue Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.length === filteredQueue.length}
                      indeterminate={selectedItems.length > 0 && selectedItems.length < filteredQueue.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(filteredQueue.map(item => item.id));
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQueue.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(item.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems([...selectedItems, item.id]);
                          } else {
                            setSelectedItems(selectedItems.filter(id => id !== item.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={item.userAvatar} sx={{ width: 32, height: 32 }} />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.userName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.userId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.type}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(item.status)}
                        label={item.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(item.status) + '20',
                          color: getStatusColor(item.status),
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.priority}
                        size="small"
                        sx={{
                          backgroundColor: getPriorityColor(item.priority) + '20',
                          color: getPriorityColor(item.priority),
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>{item.region}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(item.submittedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.expiresAt && (
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(item.expiresAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" color="primary" onClick={() => handleItemAction('review', item.id)}>
                          Review
                        </Button>
                        <Button size="small" variant="outlined" color="success" onClick={() => handleItemAction('approve', item.id)}>
                          Approve
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => handleItemAction('reject', item.id)}>
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionAnchor}
        open={Boolean(bulkActionAnchor)}
        onClose={() => setBulkActionAnchor(null)}
      >
        <MenuItemComponent onClick={() => handleBulkAction('approve')}>
          <ListItemIcon><CheckCircle /></ListItemIcon>
          <ListItemText>Approve Selected</ListItemText>
        </MenuItemComponent>
        <MenuItemComponent onClick={() => handleBulkAction('reject')}>
          <ListItemIcon><Error /></ListItemIcon>
          <ListItemText>Reject Selected</ListItemText>
        </MenuItemComponent>
        <MenuItemComponent onClick={() => handleBulkAction('notify')}>
          <ListItemIcon><NotificationsActive /></ListItemIcon>
          <ListItemText>Send Notifications</ListItemText>
        </MenuItemComponent>
      </Menu>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Compliance Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose the format for your compliance report:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="outlined" onClick={() => handleExport('pdf')}>
              PDF Report
            </Button>
            <Button variant="outlined" onClick={() => handleExport('excel')}>
              Excel Spreadsheet
            </Button>
            <Button variant="outlined" onClick={() => handleExport('csv')}>
              CSV Data
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={notificationDialogOpen} onClose={() => setNotificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notifications</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Send automated notifications to users in the verification queue
          </Typography>

          {/* Recipients Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Recipients
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={notificationSettings.recipients}
                onChange={(e) => setNotificationSettings({
                  ...notificationSettings,
                  recipients: e.target.value as any
                })}
              >
                <MenuItem value="filtered">
                  Filtered Users ({filteredQueue.length} users)
                </MenuItem>
                <MenuItem value="all">
                  All Users ({verificationQueue.length} users)
                </MenuItem>
                <MenuItem value="pending">
                  Pending Verifications Only ({verificationQueue.filter(v => v.status === 'pending').length} users)
                </MenuItem>
                <MenuItem value="expired">
                  Expired Documents Only ({verificationQueue.filter(v => v.status === 'expired').length} users)
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Notification Channels */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Notification Channels
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notificationSettings.email}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      email: e.target.checked
                    })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">Email Notifications</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Send email reminders for document verification
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notificationSettings.sms}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      sms: e.target.checked
                    })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">SMS Alerts</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Send SMS for urgent verifications
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notificationSettings.push}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      push: e.target.checked
                    })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">Push Notifications</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Send in-app push notifications for status updates
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>

          {/* Preview */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Preview</AlertTitle>
            {(() => {
              let recipientCount = 0;
              switch (notificationSettings.recipients) {
                case 'filtered':
                  recipientCount = filteredQueue.length;
                  break;
                case 'all':
                  recipientCount = verificationQueue.length;
                  break;
                case 'pending':
                  recipientCount = verificationQueue.filter(v => v.status === 'pending').length;
                  break;
                case 'expired':
                  recipientCount = verificationQueue.filter(v => v.status === 'expired').length;
                  break;
              }

              const channels = [];
              if (notificationSettings.email) channels.push('Email');
              if (notificationSettings.sms) channels.push('SMS');
              if (notificationSettings.push) channels.push('Push');

              return (
                <Typography variant="body2">
                  {channels.length > 0 ? (
                    <>
                      Will send <strong>{channels.join(', ')}</strong> to <strong>{recipientCount} user{recipientCount !== 1 ? 's' : ''}</strong>
                    </>
                  ) : (
                    'Please select at least one notification channel'
                  )}
                </Typography>
              );
            })()}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleNotificationSend}
            startIcon={<NotificationsActive />}
          >
            Send Notifications
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Regional Details Dialog */}
      <Dialog
        open={regionalDetailsOpen}
        onClose={() => setRegionalDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedRegionalData && (
              <>
                <span style={{ fontSize: '24px' }}>{selectedRegionalData.flag}</span>
                <Typography variant="h6">{selectedRegionalData.region} - Regional Compliance Details</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRegionalData && (
            <Box sx={{ mt: 2 }}>
              {/* Overall Statistics */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Overall Compliance Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedRegionalData.overall}
                    sx={{ flexGrow: 1, height: 12, borderRadius: 6 }}
                  />
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {selectedRegionalData.overall.toFixed(1)}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  {selectedRegionalData.trend > 0 ? (
                    <TrendingUp sx={{ color: '#4CAF50', fontSize: 20 }} />
                  ) : (
                    <TrendingDown sx={{ color: '#F44336', fontSize: 20 }} />
                  )}
                  <Typography
                    variant="body1"
                    sx={{ color: selectedRegionalData.trend > 0 ? '#4CAF50' : '#F44336' }}
                  >
                    {selectedRegionalData.trend > 0 ? '+' : ''}{selectedRegionalData.trend}% from last period
                  </Typography>
                </Box>
              </Box>

              {/* Compliance Breakdown */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Compliance Breakdown
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Person sx={{ color: '#2196F3', mr: 1 }} />
                          <Typography variant="subtitle2">Identity Verification</Typography>
                        </Box>
                        <Typography variant="h4" color="primary">
                          {selectedRegionalData.identity.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={selectedRegionalData.identity}
                          sx={{ mt: 1, height: 6, borderRadius: 3 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Assignment sx={{ color: '#FF9800', mr: 1 }} />
                          <Typography variant="subtitle2">Driver License</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: '#FF9800' }}>
                          {selectedRegionalData.license.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={selectedRegionalData.license}
                          sx={{ mt: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { backgroundColor: '#FF9800' } }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <DirectionsCar sx={{ color: '#4CAF50', mr: 1 }} />
                          <Typography variant="subtitle2">Vehicle Registration</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: '#4CAF50' }}>
                          {selectedRegionalData.vehicle.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={selectedRegionalData.vehicle}
                          sx={{ mt: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { backgroundColor: '#4CAF50' } }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Security sx={{ color: '#9C27B0', mr: 1 }} />
                          <Typography variant="subtitle2">Insurance Coverage</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: '#9C27B0' }}>
                          {selectedRegionalData.insurance.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={selectedRegionalData.insurance}
                          sx={{ mt: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { backgroundColor: '#9C27B0' } }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Regional Verification Queue */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Verification Queue
                </Typography>
                <Alert severity="info">
                  <AlertTitle>Queue Summary</AlertTitle>
                  <Typography variant="body2">
                    {verificationQueue.filter(v => v.region === selectedRegionalData.region).length} users in verification queue
                  </Typography>
                  <Typography variant="body2">
                    • Pending: {verificationQueue.filter(v => v.region === selectedRegionalData.region && v.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2">
                    • Approved: {verificationQueue.filter(v => v.region === selectedRegionalData.region && v.status === 'approved').length}
                  </Typography>
                  <Typography variant="body2">
                    • Rejected: {verificationQueue.filter(v => v.region === selectedRegionalData.region && v.status === 'rejected').length}
                  </Typography>
                  <Typography variant="body2">
                    • Expired: {verificationQueue.filter(v => v.region === selectedRegionalData.region && v.status === 'expired').length}
                  </Typography>
                </Alert>
              </Box>

              {/* Insights */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Key Insights
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedRegionalData.license < 80 && (
                    <Alert severity="warning">
                      Driver license compliance is below 80%. Consider targeted outreach.
                    </Alert>
                  )}
                  {selectedRegionalData.trend > 0 && (
                    <Alert severity="success">
                      Compliance is trending upward by {selectedRegionalData.trend}%.
                    </Alert>
                  )}
                  {selectedRegionalData.trend < 0 && (
                    <Alert severity="error">
                      Compliance is trending downward by {Math.abs(selectedRegionalData.trend)}%. Action required.
                    </Alert>
                  )}
                  {selectedRegionalData.overall > 85 && (
                    <Alert severity="success">
                      Excellent overall compliance rate! Keep up the good work.
                    </Alert>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegionalDetailsOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedRegionalData) {
                setSelectedRegion(selectedRegionalData.region);
                setRegionalDetailsOpen(false);
                setSnackbar({
                  open: true,
                  message: `Filtered to ${selectedRegionalData.region} region`,
                  type: 'success'
                });
              }
            }}
          >
            Filter to This Region
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default ComplianceMetricsPage;